import type { CmsLocaleInputType } from "@workspace/casino_core/schema";
import fetchFaq from "./queries/fetchFaq";
import fetchMyStatus from "./queries/fetchMyStatus";
import fetchMyTierConfiguration from "./queries/fetchMyTierConfiguration";
import type {
  BackendRequest,
  BackendState,
  RewardCenterMsg,
} from "./rewardCenter/types";
import { type Actor, withEffects } from "./system";

const MAX_ATTEMPTS = 3;
const RETRY_DELAY = 5000;

const createGraphqlBackend = (
  locale?: CmsLocaleInputType,
): Actor<BackendState, BackendRequest> => ({
  init: {
    site: "",
    status: 0,
    tiersConfiguration: 0,
    faq: 0,
  },
  receive: (state, msg) => {
    switch (msg.type) {
      case "loadTierConfiguration":
        return requestTierConfigurations(state, msg.attempt ?? 1, locale);
      case "tierConfigurationFetched":
        return withEffects(
          {
            ...state,
            tiersConfiguration: Date.now(),
          },
          {
            send: [
              "rewardCenter",
              {
                type: "tiersConfigurationLoaded",
                tiers: msg.tiers,
              } satisfies RewardCenterMsg,
            ],
          },
        );
      case "loadFaq":
        return requestFaq(state, msg.attempt ?? 1, locale);
      case "faqFetched":
        return withEffects(
          {
            ...state,
            faq: Date.now(),
          },
          {
            send: [
              "rewardCenter",
              {
                type: "faqLoaded",
                faq: msg.faq,
              } satisfies RewardCenterMsg,
            ],
          },
        );
      // Aliasing
      case "loadFrontData":
      case "loadMilestones":
      case "loadStatus":
      case "loadProgress": {
        const now = Date.now();
        if (now - state.status > 15 * 1000) {
          return requestStatus(state, msg.attempt ?? 1);
        }
        return state;
      }
      case "statusFetched":
        return withEffects(
          {
            ...state,
            status: msg.time,
          },
          {
            send: [
              "rewardCenter",
              {
                type: "statusLoaded",
                status: msg.status,
              } satisfies RewardCenterMsg,
            ],
          },
        );
      case "tiersConfigFetchFailed":
        if (msg.attempt < MAX_ATTEMPTS) {
          return withEffects(state, {
            later: [
              RETRY_DELAY * msg.attempt,
              { type: "loadTierConfiguration", attempt: msg.attempt + 1 },
              "retryTiersConfiguration",
            ],
          });
        }
        return withEffects(state, {
          send: [
            "rewardCenter",
            {
              type: "queryFailed",
              query: "tierConfiguration",
            },
          ],
        });
      case "faqFetchFailed":
        if (msg.attempt < MAX_ATTEMPTS) {
          return withEffects(state, {
            later: [
              RETRY_DELAY * msg.attempt,
              { type: "loadFaq", attempt: msg.attempt + 1 },
              "retryFaq",
            ],
          });
        }
        return withEffects(state, {
          send: [
            "rewardCenter",
            {
              type: "queryFailed",
              query: "faq",
            },
          ],
        });
      case "statusFetchFailed":
        if (msg.attempt < MAX_ATTEMPTS) {
          return withEffects(state, {
            later: [
              RETRY_DELAY * msg.attempt,
              { type: "loadStatus", attempt: msg.attempt + 1 },
              "retryStatus",
            ],
          });
        }
        return withEffects(state, {
          send: [
            "rewardCenter",
            {
              type: "queryFailed",
              query: "status",
            },
          ],
        });
      case "taskFailed":
        return withEffects(state, {
          send: [
            "rewardCenter",
            {
              type: "queryFailed",
              query: "unknown",
            },
          ],
        });
      default:
        return state;
    }
  },
});

const requestStatus = (state: BackendState, attempt: number) =>
  withEffects(
    state,
    { cancel: "retryStatus" },
    { task: [() => loadStatus(state.site, attempt), "backend"] },
  );

const loadStatus = async (
  site: string,
  attempt: number,
): Promise<BackendRequest> => {
  try {
    const status = await fetchMyStatus(site);
    if (!status) return { type: "statusFetchFailed", attempt };
    const time = Date.now();
    return {
      type: "statusFetched",
      status,
      time, // this is used to decouple
    };
  } catch (error) {
    return { type: "statusFetchFailed", attempt };
  }
};

const requestTierConfigurations = (
  state: BackendState,
  attempt: number,
  locale?: CmsLocaleInputType,
) =>
  withEffects(
    state,
    { cancel: "retryTiersConfiguration" },
    { task: [() => loadTierConfigurations(attempt, locale), "backend"] },
  );

const loadTierConfigurations = async (
  attempt: number,
  locale?: CmsLocaleInputType,
): Promise<BackendRequest> => {
  try {
    const tiers = await fetchMyTierConfiguration(locale);
    if (!tiers) return { type: "tiersConfigFetchFailed", attempt };
    return {
      type: "tierConfigurationFetched",
      tiers,
    };
  } catch (error) {
    return { type: "tiersConfigFetchFailed", attempt };
  }
};

const requestFaq = (
  state: BackendState,
  attempt: number,
  locale?: CmsLocaleInputType,
) =>
  withEffects(
    state,
    { cancel: "retryFaq" },
    { task: [() => loadFaq(attempt, locale), "backend"] },
  );

const loadFaq = async (
  attempt: number,
  locale?: CmsLocaleInputType,
): Promise<BackendRequest> => {
  try {
    const faq = await fetchFaq(locale);
    if (!faq) return { type: "faqFetchFailed", attempt };
    return {
      type: "faqFetched",
      faq,
    };
  } catch (error) {
    return { type: "faqFetchFailed", attempt };
  }
};

export { createGraphqlBackend };
export default createGraphqlBackend();
